import * as React from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp, X } from "lucide-react";

const DROPDOWN_MAX_HEIGHT_PX = 280;
const DROPDOWN_MIN_HEIGHT_PX = 120;
const DROPDOWN_GAP_PX = 4;

export type CategoryFormOption = {
  id: number;
  nome: string;
};

export interface CategoryFormSelectProps {
  id?: string;
  className?: string;
  disabled?: boolean;
  "aria-label"?: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  categories: CategoryFormOption[];
  onDeleteCategory?: (category: CategoryFormOption) => void | Promise<void>;
  showOutrasOption?: boolean;
  outrasValue?: string;
  outrasLabel?: string;
}

export function CategoryFormSelect({
  id,
  className = "",
  disabled,
  "aria-label": ariaLabel,
  placeholder = "Selecione a categoria",
  value,
  onChange,
  categories,
  onDeleteCategory,
  showOutrasOption = false,
  outrasValue = "__outra__",
  outrasLabel = "Outras",
}: CategoryFormSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [panelStyle, setPanelStyle] = React.useState<React.CSSProperties>({});
  const [deletingKeys, setDeletingKeys] = React.useState<Set<string>>(() => new Set());
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const listboxId = id ? `${id}-listbox` : undefined;
  const isOutras = value === outrasValue;

  const displayLabel = React.useMemo(() => {
    if (!value) return placeholder;
    if (isOutras) return outrasLabel;
    const cat = categories.find((c) => c.nome === value);
    return cat?.nome ?? value;
  }, [value, placeholder, isOutras, outrasLabel, categories]);

  const updatePanelPosition = React.useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - DROPDOWN_GAP_PX;
    const spaceAbove = rect.top - DROPDOWN_GAP_PX;
    const openUpward = spaceBelow < DROPDOWN_MIN_HEIGHT_PX && spaceAbove > spaceBelow;
    const availableSpace = openUpward ? spaceAbove : spaceBelow;
    const maxHeight = Math.min(
      DROPDOWN_MAX_HEIGHT_PX,
      Math.max(DROPDOWN_MIN_HEIGHT_PX, availableSpace - DROPDOWN_GAP_PX),
    );

    setPanelStyle({
      position: "fixed",
      top: openUpward ? Math.max(DROPDOWN_GAP_PX, rect.top - DROPDOWN_GAP_PX - maxHeight) : rect.bottom + DROPDOWN_GAP_PX,
      left: rect.left,
      width: rect.width,
      maxHeight,
      zIndex: 250,
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    updatePanelPosition();
    const onScrollOrResize = () => updatePanelPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open, updatePanelPosition]);

  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const selectValue = (nextValue: string) => {
    if (disabled) return;
    onChange(nextValue);
    setOpen(false);
  };

  const categoryKey = (category: CategoryFormOption) => `${category.id}:${category.nome}`;

  const handleDeleteClick = (category: CategoryFormOption, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const key = categoryKey(category);
    if (disabled || !onDeleteCategory || deletingKeys.has(key)) return;

    setDeletingKeys((prev) => new Set(prev).add(key));
    void Promise.resolve(onDeleteCategory(category)).finally(() => {
      setDeletingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    });
  };

  const triggerClassName = [
    "flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm",
    disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        className={triggerClassName}
        onClick={() => {
          if (disabled) return;
          if (!open) updatePanelPosition();
          setOpen((v) => !v);
        }}
      >
        <span className={`min-w-0 flex-1 truncate whitespace-nowrap text-left ${!value ? "text-gray-500" : ""}`}>
          {displayLabel}
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        )}
      </button>

      {open && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={panelRef}
              id={listboxId}
              role="listbox"
              aria-label={ariaLabel}
              style={panelStyle}
              className="overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white p-1.5 shadow-md"
              onWheel={(e) => e.stopPropagation()}
            >
              {categories.map((cat) => {
                const isSelected = value === cat.nome;
                const isDeleting = deletingKeys.has(categoryKey(cat));
                return (
                  <div
                    key={cat.id}
                    className={[
                      "flex w-full items-center gap-1 rounded-md transition-colors",
                      isSelected ? "bg-client-secondary" : "hover:bg-gray-50",
                    ].join(" ")}
                  >
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={[
                        "min-w-0 flex-1 truncate whitespace-nowrap rounded-md px-3 py-2 text-left text-sm",
                        isSelected ? "font-medium text-gray-900" : "text-gray-900",
                        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onClick={() => selectValue(cat.nome)}
                    >
                      {cat.nome}
                    </button>
                    {onDeleteCategory ? (
                      <button
                        type="button"
                        disabled={isDeleting}
                        className="mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-800 disabled:opacity-40"
                        aria-label={`Excluir categoria ${cat.nome}`}
                        onClick={(e) => handleDeleteClick(cat, e)}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    ) : null}
                  </div>
                );
              })}
              {showOutrasOption ? (
                <button
                  type="button"
                  role="option"
                  aria-selected={isOutras}
                  className={[
                    "block w-full truncate whitespace-nowrap rounded-md px-3 py-2 text-left text-sm transition-colors",
                    isOutras ? "bg-client-secondary font-medium text-gray-900" : "text-gray-900 hover:bg-gray-50",
                    disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => selectValue(outrasValue)}
                >
                  {outrasLabel}
                </button>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
